// Sliding-window rate limiter for query permitting.

import {
  QUERY_PERMIT_SOFT_THROTTLE_MAX_BACKOFF_MS,
  QUERY_PERMIT_SOFT_THROTTLE_MIN_BACKOFF_MS,
  QUERY_PERMIT_SOFT_THROTTLE_RESET_MS,
  QUERY_PERMIT_SOFT_THROTTLE_RPM,
  QUERY_PERMIT_SOFT_THROTTLE_WINDOW_MS,
} from './protocol';

const MS_PER_MINUTE = 60_000;

export interface SoftThrottleOptions {
  thresholdRpm?: number;
  windowMs?: number;
  minBackoffMs?: number;
  maxBackoffMs?: number;
  resetMs?: number;
}

export class QueryPermitSoftThrottle {
  private readonly recentGrants: number[] = [];
  private currentBackoffMs = 0;
  private lastCompletedGrantAt = 0;
  // Time the schedule is booked through. Concurrent grants chain off it.
  private scheduledThrough = 0;
  private declare readonly thresholdRpm: number;
  private declare readonly windowMs: number;
  private declare readonly minBackoffMs: number;
  private declare readonly maxBackoffMs: number;
  private declare readonly resetMs: number;

  constructor(opts: SoftThrottleOptions = {}) {
    this.thresholdRpm = opts.thresholdRpm ?? QUERY_PERMIT_SOFT_THROTTLE_RPM;
    this.windowMs = opts.windowMs ?? QUERY_PERMIT_SOFT_THROTTLE_WINDOW_MS;
    this.minBackoffMs = opts.minBackoffMs ?? QUERY_PERMIT_SOFT_THROTTLE_MIN_BACKOFF_MS;
    this.maxBackoffMs = opts.maxBackoffMs ?? QUERY_PERMIT_SOFT_THROTTLE_MAX_BACKOFF_MS;
    this.resetMs = opts.resetMs ?? QUERY_PERMIT_SOFT_THROTTLE_RESET_MS;
  }

  /**
   * Reserves the next permit grant and returns the backoff (in ms) the caller should sleep before
   * granting it, or 0 if no throttle should be applied. Must be paired with a matching
   * {@link recordGrant} call after the sleep, timestamped with the actual grant time, so the
   * rolling-window math stays accurate.
   *
   * Each grant is scheduled against {@link scheduledThrough}: it lands after the previous
   * reservation plus its exponential backoff.
   */
  computeBackoffMs(now: number): number {
    if (this.lastCompletedGrantAt > 0 && now - this.lastCompletedGrantAt > this.resetMs) {
      // Reset at the start of the next burst
      this.currentBackoffMs = 0;
      this.recentGrants.length = 0;
      this.scheduledThrough = 0;
    }
    this.trim(now);
    // Add 1 to include the requested permit
    const projectedRpm = ((this.recentGrants.length + 1) / this.windowMs) * MS_PER_MINUTE;
    if (projectedRpm <= this.thresholdRpm) {
      return 0;
    }
    this.currentBackoffMs =
      this.currentBackoffMs === 0 ? this.minBackoffMs : Math.min(this.currentBackoffMs * 2, this.maxBackoffMs);
    const grantAt = Math.max(now, this.scheduledThrough) + this.currentBackoffMs;
    this.scheduledThrough = grantAt;
    return grantAt - now;
  }

  recordGrant(now: number): void {
    this.recentGrants.push(now);
    this.lastCompletedGrantAt = now;
  }

  private trim(now: number): void {
    const cutoff = now - this.windowMs;
    const firstLive = this.recentGrants.findIndex((grantTime) => grantTime >= cutoff);

    if (firstLive === -1) {
      this.recentGrants.length = 0;
    } else if (firstLive > 0) {
      this.recentGrants.splice(0, firstLive);
    }
  }
}
